import React from 'react';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Trophy, TrendingUp, Award, Target, Info } from 'lucide-react';

const PointsSystemPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Award className="w-12 h-12 text-yellow-600" />
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900" data-testid="points-system-title">
              מערכת הניקוד
            </h1>
          </div>
          <p className="text-lg text-gray-600">
            מבוסס על מודל ATP עם התאמות לתחרויות שלנו
          </p>
        </div>

        {/* Daily Tournaments */}
        <Card className="mb-8 glass-effect border-2 border-blue-200" data-testid="daily-points-card">
          <CardHeader>
            <CardTitle className="text-3xl flex items-center gap-3">
              <Trophy className="w-8 h-8 text-blue-600" />
              תחרויות יומיות (16 שחקנים)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="text-right py-4 px-6 font-bold text-gray-900">מיקום</th>
                    <th className="text-center py-4 px-6 font-bold text-gray-900">נקודות</th>
                    <th className="text-right py-4 px-6 font-bold text-gray-900">פרסים נוספים</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b hover:bg-blue-50">
                    <td className="py-4 px-6 font-bold text-yellow-600">🥇 מקום 1</td>
                    <td className="py-4 px-6 text-center">
                      <span className="inline-block bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-4 py-2 rounded-full font-bold text-xl shadow-lg">
                        150 נקודות
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-700">גביע + שובר 500 ₪</td>
                  </tr>
                  <tr className="border-b hover:bg-blue-50">
                    <td className="py-4 px-6 font-bold text-gray-600">🥈 מקום 2</td>
                    <td className="py-4 px-6 text-center">
                      <span className="inline-block bg-gradient-to-r from-gray-300 to-gray-500 text-white px-4 py-2 rounded-full font-bold text-xl shadow-lg">
                        90 נקודות
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-700">גביע + שובר 250 ₪</td>
                  </tr>
                  <tr className="border-b hover:bg-blue-50">
                    <td className="py-4 px-6 font-medium">🏅 חצי גמר</td>
                    <td className="py-4 px-6 text-center">
                      <span className="inline-block bg-blue-500 text-white px-4 py-2 rounded-full font-bold text-lg shadow-md">
                        65 נקודות
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-600">הגעה לחצי גמר</td>
                  </tr>
                  <tr className="border-b hover:bg-blue-50">
                    <td className="py-4 px-6 font-medium">🎯 רבע גמר</td>
                    <td className="py-4 px-6 text-center">
                      <span className="inline-block bg-green-500 text-white px-4 py-2 rounded-full font-bold text-lg shadow-md">
                        35 נקודות
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-600">2 עולים מכל בית</td>
                  </tr>
                  <tr className="hover:bg-blue-50">
                    <td className="py-4 px-6 text-gray-600">📋 שלב בתים</td>
                    <td className="py-4 px-6 text-center">
                      <span className="inline-block bg-gray-400 text-white px-4 py-2 rounded-full font-bold shadow-md">
                        15 נקודות
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-500">השתתפות</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-4 p-4 bg-gradient-to-r from-blue-100 to-green-100 border-2 border-blue-400 rounded-lg">
              <p className="text-lg font-bold text-blue-900">
                💡 <strong>שים לב:</strong> כל שחקן מקבל רק את הנקודות של השלב הסופי שהגיע אליו.
              </p>
              <p className="text-sm text-blue-800 mt-1">
                לדוגמה: מנצח מקבל רק <strong>150 נקודות</strong> (לא צבירה מכל השלבים)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Knockout Tournaments */}
        <Card className="mb-8 glass-effect" data-testid="knockout-points-card">
          <CardHeader>
            <CardTitle className="text-3xl flex items-center gap-3">
              <Trophy className="w-8 h-8 text-green-600" />
              תחרויות נוקאאוט (עד 32 שחקנים)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-green-50">
                  <tr>
                    <th className="text-right py-4 px-6 font-bold text-gray-900">מיקום</th>
                    <th className="text-center py-4 px-6 font-bold text-gray-900">נקודות</th>
                    <th className="text-right py-4 px-6 font-bold text-gray-900">פרסים נוספים</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b hover:bg-green-50">
                    <td className="py-4 px-6 font-bold text-yellow-600">🥇 מקום 1</td>
                    <td className="py-4 px-6 text-center">
                      <span className="inline-block bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full font-bold text-xl">
                        350 נקודות
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-700">גביע + שובר 1,000 ₪</td>
                  </tr>
                  <tr className="border-b hover:bg-green-50">
                    <td className="py-4 px-6 font-bold text-gray-600">🥈 מקום 2</td>
                    <td className="py-4 px-6 text-center">
                      <span className="inline-block bg-gray-100 text-gray-800 px-4 py-2 rounded-full font-bold text-xl">
                        250 נקודות
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-700">שובר 500 ₪</td>
                  </tr>
                  <tr className="border-b hover:bg-green-50">
                    <td className="py-4 px-6 font-bold text-orange-600">🥉 מקום 3</td>
                    <td className="py-4 px-6 text-center">
                      <span className="inline-block bg-orange-100 text-orange-800 px-4 py-2 rounded-full font-bold text-lg">
                        170 נקודות
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-700">שובר 250 ₪</td>
                  </tr>
                  <tr className="border-b hover:bg-green-50">
                    <td className="py-4 px-6 font-medium">🏅 מקום 4</td>
                    <td className="py-4 px-6 text-center">
                      <span className="inline-block bg-purple-100 text-purple-800 px-4 py-2 rounded-full font-bold">
                        120 נקודות
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-600">חצי גמר</td>
                  </tr>
                  <tr className="border-b hover:bg-green-50">
                    <td className="py-4 px-6 font-medium">🎯 רבע גמר</td>
                    <td className="py-4 px-6 text-center">
                      <span className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full font-bold">
                        80 נקודות
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-600">רבע הגמר</td>
                  </tr>
                  <tr className="border-b hover:bg-green-50">
                    <td className="py-4 px-6">📊 שמינית גמר</td>
                    <td className="py-4 px-6 text-center">
                      <span className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-bold">
                        50 נקודות
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-600">מתוך 16 הטובים</td>
                  </tr>
                  <tr className="hover:bg-green-50">
                    <td className="py-4 px-6 text-gray-600">📋 סיבוב ראשון</td>
                    <td className="py-4 px-6 text-center">
                      <span className="inline-block bg-gray-50 text-gray-600 px-4 py-2 rounded-full">
                        25 נקודות
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-500">מתוך 32 המשתתפים</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-lg font-bold text-green-900">
                💡 <strong>שים לב:</strong> כל שחקן מקבל רק את הנקודות של השלב הסופי שהגיע אליו.
              </p>
              <p className="text-sm text-green-800 mt-1">
                לדוגמה: מנצח מקבל רק <strong>350 נקודות</strong> (לא צבירה מכל השלבים)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Examples */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="card-hover border-2 border-yellow-300">
            <CardHeader className="bg-gradient-to-br from-yellow-400 to-yellow-500 text-white">
              <CardTitle className="text-xl">דוגמה 1: מנצח יומית 🥇</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">השלב שהגיע אליו:</span>
                  <span className="font-bold text-yellow-600">🥇 מקום 1</span>
                </div>
                <div className="border-t-2 pt-3 flex justify-between items-center font-bold text-lg">
                  <span>נקודות:</span>
                  <span className="text-yellow-600 text-2xl">150</span>
                </div>
                <p className="text-xs text-gray-500 italic mt-2">
                  זכה בתחרות = 150 נקודות בלבד
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover border-2 border-blue-300">
            <CardHeader className="bg-gradient-to-br from-blue-400 to-blue-500 text-white">
              <CardTitle className="text-xl">דוגמה 2: חצי גמר יומית 🏅</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">השלב שהגיע אליו:</span>
                  <span className="font-bold text-blue-600">🏅 חצי גמר</span>
                </div>
                <div className="border-t-2 pt-3 flex justify-between items-center font-bold text-lg">
                  <span>נקודות:</span>
                  <span className="text-blue-600 text-2xl">65</span>
                </div>
                <p className="text-xs text-gray-500 italic mt-2">
                  הפסיד בחצי גמר = 65 נקודות בלבד
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover border-2 border-green-300">
            <CardHeader className="bg-gradient-to-br from-green-400 to-green-500 text-white">
              <CardTitle className="text-xl">דוגמה 3: מנצח נוקאאוט 🏆</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">השלב שהגיע אליו:</span>
                  <span className="font-bold text-yellow-600">🥇 מקום 1</span>
                </div>
                <div className="border-t-2 pt-3 flex justify-between items-center font-bold text-lg">
                  <span>נקודות:</span>
                  <span className="text-green-600 text-2xl">350</span>
                </div>
                <p className="text-xs text-gray-500 italic mt-2">
                  זכה בתחרות = 350 נקודות בלבד
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tips */}
        <Card className="glass-effect">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-3">
              <Target className="w-7 h-7 text-purple-600" />
              טיפים לצבירת נקודות
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-yellow-50 rounded-lg border-2 border-yellow-300">
              <h3 className="font-bold text-lg mb-2 text-yellow-900">⚠️ חשוב להבין!</h3>
              <p className="text-gray-700">
                מערכת הניקוד מבוססת על <strong>השלב הסופי</strong> בלבד. אם הגעת לחצי גמר - תקבל את נקודות חצי הגמר בלבד (לא צבירה של כל השלבים הקודמים).
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
              <h3 className="font-bold text-lg mb-2 text-blue-900">💪 אסטרטגיה 1: עקביות</h3>
              <p className="text-gray-700">
                משחק בהרבה תחרויות יומיות והשתדל להגיע לרבע גמר לפחות - 10 תחרויות ברבע גמר = <strong className="text-blue-600">350 נקודות!</strong>
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="font-bold text-lg mb-2 text-green-900">🏆 אסטרטגיה 2: איכות</h3>
              <p className="text-gray-700">
                התמקדות בתחרויות נוקאאוט והגעה למקומות גבוהים - מקום 1 בנוקאאוט = <strong>350 נקודות!</strong>
                שווה כמו <strong>2.3 זכיות</strong> בתחרויות יומיות.
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <h3 className="font-bold text-lg mb-2 text-purple-900">🎯 אסטרטגיה 3: השוואה מהירה</h3>
              <div className="text-sm text-gray-700 space-y-1">
                <p>• זכייה יומית (150) = רבע גמר נוקאאוט (80) + חצי גמר יומית (65)</p>
                <p>• זכייה נוקאאוט (350) = 2.3 זכיות יומיות</p>
                <p>• מקום 3 נוקאאוט (170) ≈ זכייה יומית + רבע גמר יומית</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PointsSystemPage;
